/**
 * Lab Betfair Balance Dashboard — domain types + pure helpers.
 * Phase 1 uses fixture data; later wired to CM-API
 * `POST /v1/book/betfair-market-event-exposure`.
 */

export const BALANCE_SPORTS = [
  'ALL',
  'Cricket',
  'Soccer',
  'Tennis',
  'Horse Racing',
  'Greyhound Racing',
] as const

export type BalanceSport = (typeof BALANCE_SPORTS)[number]

export const BALANCE_PAGE_SIZES = [10, 25] as const
export type BalancePageSize = (typeof BALANCE_PAGE_SIZES)[number]

/** Lab `getBetfairMarketEventExposure` row shape. */
export interface BetfairBalanceRow {
  day: string
  username: string
  eventtype: string
  event: string
  eventid: string
  betfair_bets: number
  betfair_stake_used: number
  net_exposure: number
}

export interface BetfairBalancePage {
  results: BetfairBalanceRow[]
  count: number
  page: number
  maxRow: number
}

export interface BetfairBalanceQuery {
  sport: BalanceSport
  page: number
  maxRow: BalancePageSize
}

/**
 * Lab `formatDay`: YYYY-MM-DD → "Sep 29, 2026".
 * ISO timestamps use IST (+330) like Lab moment.utcOffset(330).
 */
export function formatBalanceDay(day: string | null | undefined): string {
  if (!day) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    const parts = day.split('-').map(Number)
    const y = parts[0]!
    const m = parts[1]!
    const d = parts[2]!
    const dt = new Date(y, m - 1, d)
    return DAY_FMT.format(dt)
  }
  const dt = new Date(day)
  if (Number.isNaN(dt.getTime())) return ''
  const ist = new Date(dt.getTime() + 330 * 60_000)
  return DAY_FMT.format(
    new Date(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()),
  )
}

const DAY_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
})

/** Filter + paginate Lab-style (sport ALL = no filter). */
export function queryBalanceRows(
  rows: BetfairBalanceRow[],
  query: BetfairBalanceQuery,
): BetfairBalancePage {
  const sport = query.sport
  const filtered =
    sport === 'ALL'
      ? rows
      : rows.filter((r) => r.eventtype.toLowerCase() === sport.toLowerCase())

  const maxRow = query.maxRow
  const page = Math.max(1, query.page)
  const start = (page - 1) * maxRow
  return {
    results: filtered.slice(start, start + maxRow),
    count: filtered.length,
    page,
    maxRow,
  }
}
